import { z } from 'zod';
import { OUTBOUND_CALL_OUTCOME_VALUES } from '@vagusx/shared';
import { DATE_RANGE_PRESETS } from '@/lib/date-range';

/**
 * URL search-param schema for Call History.
 *
 * EVERY FILTER TRAVELS THROUGH THE URL, so a reload, a Back, or a link
 * pasted to a colleague reproduces the list somebody was working. Holding
 * them in component state instead meant the address bar said `/calls`
 * no matter what was on screen — the same thing `/referrals` and
 * `/documents` moved their state out of state to stop.
 *
 * The enums `.catch(undefined)` rather than reject: a hand-edited or stale
 * link should degrade to the unfiltered list, not fail the whole route. It
 * also keeps a junk value from reaching `GET /api/interactions/list`, which
 * validates both vocabularies and 422s.
 */
export const callHistorySearchSchema = z.object({
  /**
   * Interaction whose Call detail sheet is open. Written by
   * `useCallDetailParam`; must be declared here or the router strips it and
   * the transcript sheet never opens.
   */
  call: z.string().uuid().optional(),
  direction: z.enum(['inbound', 'outbound']).optional().catch(undefined),
  outcome: z.enum(OUTBOUND_CALL_OUTCOME_VALUES).optional().catch(undefined),
  /** Named calendar period. Absent means All time. */
  period: z.enum(DATE_RANGE_PRESETS).optional().catch(undefined),
  /** `yyyy-MM-dd` bounds, carried only when `period` is `custom`. */
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
  searchTerm: z.string().optional(),
});

export type CallHistorySearch = z.infer<typeof callHistorySearchSchema>;
